/// <reference path="./types/style-modules.d.ts" />
/// <reference path="./types/office-globals.d.ts" />
/// <reference path="./types/webpack-globals.d.ts" />
import "./utils/ensurePromiseAllSettledPolyfill";
import "./styles/tokens.css";
import "./styles/taskpaneLayout.scss";
import "./styles/shell.scss";
import * as React from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "./components/App";
import { ThemeProvider } from "./theme/ThemeProvider";
import { ThemedConfigProvider } from "./ThemedConfigProvider";
import { enableDocumentReviewMode } from "./agreement";

const title = "Cenna";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false },
  },
});

const rootElement: HTMLElement | null = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;

const shell = (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <ThemedConfigProvider>
        <App title={title} />
      </ThemedConfigProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

/**
 * Tag the document so Word auto-opens this task pane on the next open (and thereafter),
 * per https://learn.microsoft.com/en-us/office/dev/add-ins/develop/automatically-open-a-task-pane-with-a-document
 * Requires manifest TaskpaneId Office.AutoShowTaskpaneWithDocument. Only writes once per document when unset.
 */
function persistAutoOpenTaskpaneSetting(): void {
  try {
    if (Office.context.document == null) {
      return;
    }
    const settings = Office.context.document.settings;
    if (settings.get("Office.AutoShowTaskpaneWithDocument") === true) {
      return;
    }
    settings.set("Office.AutoShowTaskpaneWithDocument", true);
    settings.saveAsync();
  } catch {
    // Settings API unavailable in some hosts or modes
  }
}

/* Render application after Office initializes */
Office.onReady((info) => {
  if (info.host === Office.HostType.Word) {
    persistAutoOpenTaskpaneSetting();
    enableDocumentReviewMode()
      .then(() =>
        Word.run(async (context) => {
          await context.document.save();
        })
      )
      .catch(() => {
        // Swallow errors during reload; enableDocumentReviewMode already logs internally.
      });
  }
  root?.render(shell);
});

if ((module as any).hot) {
  (module as any).hot.accept("./components/App", () => {
    const NextApp = require("./components/App").default;
    root?.render(
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <ThemedConfigProvider>
            <NextApp title={title} />
          </ThemedConfigProvider>
        </ThemeProvider>
      </QueryClientProvider>
    );
  });
}
